/* assets/ebc-sections.js
 * Hydrates EBC blocks marked with `has_lazy_load` on the metaobject entry,
 * and swaps the section when the selected variant changes.
 *
 * Variant content is rendered by Liquid (variant metafield, then product).
 * The theme's variant picker refetches the product page and dispatches
 * `variant:update` with that HTML. This element copies its own updated node
 * from that document so variant-level EBC replaces product-level EBC.
 *
 * Lazy content sits inside an inert <template>, so nothing inside it — images,
 * iframes, video, stylesheets — is fetched until the block nears the viewport.
 * On hydration the fragment is cloned in and any <script> tags are re-created
 * so the browser will actually execute them (cloned scripts do not run).
 */

class EbcSections extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    document.addEventListener('variant:update', this.onVariantUpdate, {
      signal: this.abortController.signal,
    });
    this.bindLazy();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.teardownLazy();
  }

  /**
   * @param {CustomEvent} event
   */
  onVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    if (!html || typeof html.querySelector !== 'function') return;

    const incomingProductId = event.detail?.data?.productId;
    if (
      incomingProductId &&
      this.dataset.productId &&
      String(incomingProductId) !== this.dataset.productId
    ) {
      return;
    }

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const next = html.querySelector(
      `ebc-sections[data-section-id="${sectionId.replaceAll('"', '\\"')}"]`
    );
    if (!(next instanceof HTMLElement)) return;

    this.applySnapshot(next);
  };

  /**
   * @param {HTMLElement} next
   */
  applySnapshot(next) {
    this.teardownLazy();

    this.hidden = next.hasAttribute('hidden');
    if (next.dataset.variantId) this.dataset.variantId = next.dataset.variantId;
    if (next.dataset.ebcSource) this.dataset.ebcSource = next.dataset.ebcSource;
    if (next.dataset.rootMargin) this.dataset.rootMargin = next.dataset.rootMargin;

    this.innerHTML = next.innerHTML;
    this.activateScripts(this);
    this.bindLazy();
  }

  /**
   * innerHTML does not run scripts. Rebuild eager ones. Scripts inside
   * <template> are not matched here and stay inert until hydrate().
   * @param {ParentNode} root
   */
  activateScripts(root) {
    root.querySelectorAll('script').forEach((oldScript) => {
      const script = document.createElement('script');
      for (const attr of oldScript.attributes) {
        script.setAttribute(attr.name, attr.value);
      }
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  }

  bindLazy() {
    const pending = this.querySelectorAll('[data-ebc-lazy]');
    if (!pending.length) return;

    if (!('IntersectionObserver' in window)) {
      pending.forEach((el) => this.hydrate(el));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          obs.unobserve(entry.target);
          this.hydrate(entry.target);
        }
      },
      { rootMargin: this.dataset.rootMargin || '400px' }
    );

    pending.forEach((el) => this.observer.observe(el));

    this.flush = () => pending.forEach((el) => this.hydrate(el));
    window.addEventListener('beforeprint', this.flush);
  }

  teardownLazy() {
    this.observer?.disconnect();
    this.observer = undefined;
    if (this.flush) {
      window.removeEventListener('beforeprint', this.flush);
      this.flush = undefined;
    }
  }

  /**
   * @param {Element} host
   */
  hydrate(host) {
    if (!host || host.hasAttribute('data-ebc-ready')) return;

    const tpl = host.querySelector(':scope > template[data-ebc-content]');
    if (!tpl || !('content' in tpl)) return;

    const frag = tpl.content.cloneNode(true);

    frag.querySelectorAll('script').forEach((oldScript) => {
      const script = document.createElement('script');
      for (const attr of oldScript.attributes) {
        script.setAttribute(attr.name, attr.value);
      }
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });

    tpl.remove();
    host.appendChild(frag);

    host.setAttribute('data-ebc-ready', '');
    host.removeAttribute('data-ebc-lazy');
    if (host instanceof HTMLElement) host.style.minHeight = '';

    host.dispatchEvent(
      new CustomEvent('ebc:loaded', {
        bubbles: true,
        detail: { id: host.id, label: host.dataset.ebcLabel },
      })
    );
  }
}

if (!customElements.get('ebc-sections')) {
  customElements.define('ebc-sections', EbcSections);
}
